/**
 * Inventory Imports — admin-only landing for bulk RM + FG import workflows.
 *
 * Moved here from RawMaterials.js and Inventory.js (May 2026) so day-to-day
 * users can't accidentally hit destructive bulk operations. Two rows: RM
 * inventory + FG inventory. Each row has the same two buttons that used to
 * live on the inventory pages: "Download Template" and "Import".
 *
 * The import flow itself is reused verbatim — same endpoints, same modes
 * ("Add" / "Replace Stock"), same pre-replace snapshot safety net.
 */
import { useState } from "react";
import axios from "axios";
import { Download, Upload, AlertCircle, CheckCircle, Loader2, Package, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ── Per-kind config — easy to extend if a third inventory kind shows up ──
const KINDS = {
  RM: {
    label: "Raw Materials",
    icon: Package,
    templateUrl: `${API}/api/inventory/rm/template`,
    importUrl: (mode) => `${API}/api/inventory/rm/bulk-import?mode=${mode}`,
    snapshotUrl: `${API}/api/raw-materials/export-all`, // best-effort; if absent the snapshot step degrades to a warning
    templateFilename: "rm_inventory_template.xlsx",
    snapshotFilename: (ts) => `rm_inventory_PRE_REPLACE_${ts}.xlsx`,
    testIdPrefix: "rm",
  },
  FG: {
    label: "Finished Goods",
    icon: Warehouse,
    templateUrl: `${API}/api/inventory/fg/template`,
    importUrl: (mode) => `${API}/api/inventory/fg/bulk-import?mode=${mode}`,
    snapshotUrl: `${API}/api/inventory/fg/export`,
    templateFilename: "fg_inventory_template.xlsx",
    snapshotFilename: (ts) => `fg_inventory_PRE_REPLACE_${ts}.xlsx`,
    testIdPrefix: "fg",
  },
};

export default function InventoryImports() {
  const [importKind, setImportKind] = useState(null); // 'RM' | 'FG' | null
  const [importMode, setImportMode] = useState("add");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const downloadTemplate = async (kind) => {
    const cfg = KINDS[kind];
    try {
      const res = await axios.get(cfg.templateUrl, {
        headers: getHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", cfg.templateFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${cfg.label} template downloaded`);
    } catch (e) {
      toast.error(`Failed to download ${cfg.label} template`);
    }
  };

  const downloadSnapshot = async (kind) => {
    const cfg = KINDS[kind];
    try {
      const res = await axios.get(cfg.snapshotUrl, {
        headers: getHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      link.href = url;
      link.setAttribute("download", cfg.snapshotFilename(ts));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const openImportDialog = (kind) => {
    setImportKind(kind);
    setImportMode("add");
    setImportFile(null);
    setImportResult(null);
  };

  const closeImportDialog = () => {
    if (importing) return;
    setImportKind(null);
    setImportFile(null);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!importKind) return;
    if (!importFile) { toast.error("Please select a file"); return; }
    const cfg = KINDS[importKind];

    setImporting(true);
    setImportResult(null);

    // Replace mode safety-net: download a pre-import snapshot so the user
    // always has a rollback baseline. If the snapshot fails, abort.
    if (importMode === "replace") {
      const ok = await downloadSnapshot(importKind);
      if (!ok) {
        toast.error(
          "Couldn't download the pre-import snapshot. Aborting Replace to protect your data — retry or use 'Add to Existing' mode.",
          { duration: 8000 }
        );
        setImporting(false);
        return;
      }
      toast.info("Pre-import snapshot downloaded. Keep that file safe — it's your rollback baseline.", { duration: 6000 });
    }

    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const { uploadWithReceipt } = await import("../lib/bulkUploadReceipt");
      const data = await uploadWithReceipt(
        cfg.importUrl(importMode),
        formData,
        {
          axiosConfig: {
            headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
          },
          showToast: false,
        }
      );
      setImportResult(data);
      if (data.success) {
        toast.success(`${cfg.label} import complete: ${data.processed} records processed — Receipt downloaded.`);
      } else {
        toast.error("Import completed with errors — see downloaded receipt for details.");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Import failed";
      toast.error(msg);
      setImportResult({ errors: [msg], success: false });
    }
    setImporting(false);
  };

  const renderRow = (kind) => {
    const cfg = KINDS[kind];
    const Icon = cfg.icon;
    return (
      <div
        key={kind}
        className="border border-zinc-200 rounded-lg p-5 flex items-center justify-between gap-4 bg-white"
        data-testid={`inventory-imports-row-${cfg.testIdPrefix}`}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-md bg-zinc-900 text-white flex items-center justify-center">
            <Icon className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-bold text-base uppercase tracking-tight">{cfg.label}</div>
            <div className="text-xs text-muted-foreground font-mono">
              {kind === "RM" ? "Branch-scoped RM stock" : "Branch-scoped FG (buyer SKU) stock"}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => downloadTemplate(kind)}
            className="uppercase text-xs tracking-wide"
            data-testid={`inventory-imports-${cfg.testIdPrefix}-template-btn`}
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Download Template
          </Button>
          <Button
            onClick={() => openImportDialog(kind)}
            className="uppercase text-xs tracking-wide"
            data-testid={`inventory-imports-${cfg.testIdPrefix}-import-btn`}
          >
            <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Import
          </Button>
        </div>
      </div>
    );
  };

  const dialogCfg = importKind ? KINDS[importKind] : null;

  return (
    <div className="p-6 md:p-8" data-testid="inventory-imports-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight uppercase">Inventory Imports</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono">
          Admin-only bulk import workflows for Raw Material &amp; Finished Goods stock.
        </p>
      </div>

      <div className="space-y-3 max-w-4xl">
        {renderRow("RM")}
        {renderRow("FG")}
      </div>

      {/* Shared import dialog — kind-aware */}
      <Dialog open={!!importKind} onOpenChange={(open) => { if (!open) closeImportDialog(); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-tight">
              Import {dialogCfg?.label || ""}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide mb-1 block">Mode</label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger data-testid="inventory-imports-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add to Existing (safe — adjusts stock by delta)</SelectItem>
                  <SelectItem value="replace">Replace Stock (destructive — overwrites)</SelectItem>
                </SelectContent>
              </Select>
              {importMode === "replace" && (
                <div className="mt-2 flex gap-2 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Replace will overwrite the current stock with the values in the file.
                    A pre-import snapshot is auto-downloaded as a rollback baseline.
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wide mb-1 block">Excel File</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="block w-full text-sm border border-zinc-200 rounded px-2 py-1.5"
                data-testid="inventory-imports-file-input"
              />
              {importFile && (
                <p className="text-xs text-zinc-600 mt-1 font-mono">
                  {importFile.name} ({(importFile.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>

            {importResult && (
              <div className={`p-3 rounded border text-xs ${importResult.success ? "bg-green-50 border-green-200 text-green-900" : "bg-red-50 border-red-200 text-red-900"}`}>
                <div className="flex items-center gap-2 font-semibold mb-1">
                  {importResult.success
                    ? <CheckCircle className="w-4 h-4" />
                    : <AlertCircle className="w-4 h-4" />}
                  {importResult.success ? "Import complete" : "Import completed with issues"}
                </div>
                {typeof importResult.processed === "number" && (
                  <div>Processed: {importResult.processed}</div>
                )}
                {Array.isArray(importResult.errors) && importResult.errors.length > 0 && (
                  <ul className="list-disc pl-5 mt-1">
                    {importResult.errors.slice(0, 6).map((er, i) => (<li key={i}>{String(er)}</li>))}
                  </ul>
                )}
              </div>
            )}

            <Button
              variant="link"
              size="sm"
              onClick={() => dialogCfg && downloadTemplate(importKind)}
              className="text-xs px-0"
              data-testid="inventory-imports-dialog-template-link"
            >
              <Download className="w-3 h-3 mr-1" />
              Download {dialogCfg?.label} template
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeImportDialog} disabled={importing}>Cancel</Button>
            <Button
              onClick={handleImport}
              disabled={!importFile || importing}
              data-testid="inventory-imports-confirm-btn"
            >
              {importing
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</>
                : <><Upload className="w-4 h-4 mr-2" /> Import</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
